import { useState, useEffect, useRef } from 'react';
import { BOT_CONFIG } from '../config/botConfig';

// Trava de sessão para evitar que múltiplos timers rodem para o mesmo ticket no mesmo navegador
const activeTimers = new Set();

/**
 * Hook para gerenciar o Assistente Virtual Automático (Client-Side)
 * Lógica restaurada para rodar no navegador.
 */
/**
 * Hook para gerenciar o Assistente Virtual Automático (Server-Side Logic via Client)
 * Agora focado apenas na execução lógica via TicketBotManager.
 */
export const useTicketBot = (ticket, user, addResponse, updateTicket, claimBotTask) => {
    const ticketRef = useRef(ticket);
    const userRef = useRef(user);
    const inFlightActions = useRef(new Set());

    useEffect(() => {
        ticketRef.current = ticket;
    }, [ticket]);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const getBotState = () => {
        if (!ticketRef.current) return { status: 'completed' };

        const state = ticketRef.current.bot_state || ticketRef.current.botState;
        if (!state || Object.keys(state).length === 0 || !state.status) {
            return {
                status: 'pending',
                interactionsCount: 0,
                currentMissingField: null,
                pendingAction: null
            };
        }
        return state;
    };

    const updateBotState = async (newState) => {
        const currentState = getBotState();
        const updatedState = { ...currentState, ...newState };
        await updateTicket(ticketRef.current.id, { bot_state: updatedState });
    };

    // Helper para enviar mensagem do bot
    const sendBotMessage = async (message, nextState = {}, typingDuration = BOT_CONFIG.NORMAL_TYPING_DELAY) => {
        // Indica visualmente que o bot está digitando (atualiza DB para todos verem)
        try {
            await updateTicket(ticketRef.current.id, { is_bot_typing: true });
        } catch (e) { console.error("Erro ao setar typing:", e); }

        await new Promise(r => setTimeout(r, typingDuration));

        try {
            await updateTicket(ticketRef.current.id, { is_bot_typing: false });
        } catch (e) { console.error("Erro ao remover typing:", e); }

        console.log("Adding response to ticket:", ticketRef.current.id, message);
        await addResponse(ticketRef.current.id, message, false, 'Agente de Verificação');

        if (Object.keys(nextState).length > 0) {
            await updateBotState(nextState);
        } else if (nextState === null) {
            // Null significa que acabou - libera para staff
            await updateTicket(ticketRef.current.id, {
                status: 'aberto',
                auxiliary_status: [...(ticketRef.current.auxiliary_status || []), 'aguardando_staff'],
                bot_state: { status: 'completed' }
            });
        }
    };

    // Helper para encontrar configuração da categoria
    const getCategoryConfig = (category) => {
        const cats = BOT_CONFIG.CATEGORIES;
        if (cats[category]) return cats[category];
        const foundKey = Object.keys(cats).find(key =>
            cats[key].aliases && cats[key].aliases.includes(category)
        );
        return foundKey ? cats[foundKey] : null;
    };

    // 1. Hook Principal - Processar Novo Ticket (Greeting)
    useEffect(() => {
        if (!ticketRef.current || ticketRef.current.status !== 'aguardando_sistema' || ticketRef.current.assigned_to) return;

        const currentState = getBotState();
        if (currentState.status !== 'pending') return;

        // Note: Strict Mode resilience logic is handled by the atomic claim check below.
        // Even if this effect runs twice, only one will succeed in claiming.

        const runGreeting = async () => {
            try {
                const config = getCategoryConfig(ticketRef.current.category);

                // Se não tem config, libera direto
                if (!config) {
                    await updateBotState({ status: 'completed' });
                    await updateTicket(ticketRef.current.id, {
                        status: 'aberto',
                        auxiliary_status: [...(ticketRef.current.auxiliary_status || []), 'aguardando_staff']
                    });
                    return;
                }

                // Tentar reivindicar o ticket para evitar duplicidade (Trava Atômica)
                let claimResult = { success: false };
                if (typeof claimBotTask === 'function') {
                    console.log(`[Bot Manager] Tentando reivindicar ticket ${ticketRef.current.id}...`);
                    claimResult = await claimBotTask(ticketRef.current.id);
                }

                // Se NÃO conseguiu a trava, verifica se é um erro de permissão ou se realmente já foi pego
                if (!claimResult.success) {
                    // Fallback: Se o estado local ainda é 'pending', pode ser que a trava falhou por erro de RLS/Syntax mas ninguém assumiu.
                    // Tentamos prosseguir com cautela.
                    const freshState = getBotState();
                    if (freshState.status === 'pending' && !ticketRef.current.assigned_to) {
                        console.warn(`[Bot Manager] Falha na trava (Lock) para ${ticketRef.current.id}, mas estado ainda é pending. Forçando execução (Bypass RLS).`);
                    } else {
                        console.log(`[Bot Manager] Ticket ${ticketRef.current.id} já processado ou bloqueado. Abortando.`);
                        return;
                    }
                }

                // SE CONSEGUIU A TRAVA OU MANUAL BYPASS:
                console.log(`[Bot Manager] Processando Greeting para ${ticketRef.current.id}...`);

                // Delay Inicial
                await new Promise(r => setTimeout(r, BOT_CONFIG.INITIAL_START_DELAY));

                // Re-verificar estado após delay
                if (ticketRef.current.status !== 'aguardando_sistema' || ticketRef.current.assigned_to) {
                    console.log("[Bot Manager] Ticket assumido por staff ou status mudou. Abortando.");
                    return;
                }

                // [NEW] Verificação de Segurança: O bot já respondeu?
                // Se houver respostas e a última for do bot (ou se houver qualquer resposta do bot), não mandar Hello dnv.
                const hasBotResponse = ticketRef.current.responses?.some(r => r.isBot || r.userId === 'system' || r.userId === 'system_bot_security');
                if (hasBotResponse) {
                    console.log("[Bot Manager] Detectado que o bot já respondeu este ticket. Atualizando estado para 'waiting_input' e abortando greeting.");
                    await updateBotState({ status: 'waiting_input', pendingAction: 'recovered_state' });
                    return;
                }

                // Analisar Campos Obrigatórios
                let missingField = null;
                if (config.mandatoryFields) {
                    missingField = config.mandatoryFields.find(field => {
                        const val = ticketRef.current.formData?.[field.key] || ticketRef.current[field.key];
                        return !val || String(val).trim() === '';
                    });
                }

                const charName = ticketRef.current.formData?.characterName || ticketRef.current.character_name || 'Jogador';
                let greetingMsg = '';
                let nextBotState = {};

                if (missingField) {
                    greetingMsg = config.scripts.GREETING_MISSING(charName, missingField.label);
                    nextBotState = {
                        status: 'waiting_input',
                        pendingAction: 'awaiting_data',
                        currentMissingField: missingField
                    };
                } else {
                    greetingMsg = config.scripts.GREETING_OK(charName);
                    if (config.isInteractive) {
                        nextBotState = {
                            status: 'waiting_input',
                            pendingAction: 'interactive_flow',
                            currentStep: 1
                        };
                    } else {
                        nextBotState = null;
                    }
                }

                await sendBotMessage(greetingMsg, nextBotState, BOT_CONFIG.INITIAL_TYPING_DURATION);
                console.log(`[Bot Manager] Resposta inicial enviada para o ticket ${ticketRef.current.id}.`);

            } catch (error) {
                console.error("Erro no Bot Manager:", error);
            }
        };

        runGreeting();

    }, [ticket?.status, ticket?.id]);

    // 2. Failsafe - Timeout para liberar ticket se o bot travar
    useEffect(() => {
        const stateStatus = getBotState().status;
        // Verify if category is super_cupom to avoid auto-forwarding
        const isSuperCupom = ticket?.category === 'super_cupom' || ticket?.category === 'cupons_descontos'; // Safety check

        if (ticket?.status === 'aguardando_sistema' && stateStatus !== 'completed' && !ticket.assigned_to && !isSuperCupom) {
            const timeoutId = setTimeout(async () => {
                console.warn(`[Bot Failsafe] Liberando ticket ${ticket.id} travado no bot (Status: ${stateStatus}).`);
                await updateBotState({ status: 'completed' });
                await updateTicket(ticket.id, {
                    status: 'aberto',
                    auxiliary_status: [...(ticket.auxiliary_status || []), 'aguardando_staff']
                });
            }, 120000); // 2 minutos
            return () => clearTimeout(timeoutId);
        }
    }, [ticket?.status, ticket?.bot_state?.status, ticket?.category]);

    // 3. Monitorar Respostas do Usuário e Interações
    useEffect(() => {
        const responses = ticketRef.current?.responses || [];
        if (responses.length === 0) return;

        const lastResponse = responses[responses.length - 1];
        const state = getBotState();

        console.log('[Bot Activity] New Response detected:', {
            msg: lastResponse.message,
            isBot: lastResponse.isBot,
            isInternal: lastResponse.isInternal,
            isTyping: ticketRef.current?.is_bot_typing,
            assigned: ticketRef.current?.assigned_to,
            state: state
        });

        // Check if currently typing (via DB prop, though we are the ones setting it usually)
        // But we can check local intention or just proceed.
        const isBotTyping = ticketRef.current?.is_bot_typing;

        // Se a última mensagem NÃO é do bot (é do usuário) E não estamos digitando E não tem staff
        if (!lastResponse.isBot && !isBotTyping && !ticketRef.current?.assigned_to) {

            // Verificar se bot está esperando input
            if (state.status === 'waiting_input' && state.pendingAction === 'awaiting_data' && state.currentMissingField) {

                const processUserResponse = async () => {
                    const field = state.currentMissingField;

                    // Validação de URL
                    if (field.type === 'url') {
                        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/;
                        if (!urlRegex.test(lastResponse.message)) {
                            await sendBotMessage("Isso não parece ser um link válido. Por favor, tente novamente.", {}, 3000);
                            return;
                        }
                    }

                    // Sucesso!
                    const successMsg = BOT_CONFIG.GLOBAL_SCRIPTS.HANDOFF_COMPLETE;
                    await sendBotMessage(successMsg, null, BOT_CONFIG.NORMAL_TYPING_DELAY);
                };

                processUserResponse();
            }

            // [NEW] Lógica de Fluxo Interativo (Super Cupom etc)
            if (state.status === 'waiting_input' && state.pendingAction === 'interactive_flow') {
                // Tenta capturar ACTION tag OU comando direto (ex: sc_gerar_acesso)
                const actionMatch = lastResponse.message.match(/\[ACTION:([^\]]+)\]/);
                const rawAction = lastResponse.message.trim();

                let action = actionMatch ? actionMatch[1] : (rawAction.startsWith('sc_') ? rawAction : null);

                // [NEW] Natural Language Matches with Hashtags
                if (!action) {
                    if (lastResponse.message.includes('#AcessoLiberar')) action = 'sc_gerar_acesso';
                    if (lastResponse.message.includes('#FalarComAtendente')) action = 'sc_falar_atendente';
                    if (lastResponse.message.includes('#CompraConcluida')) action = 'sc_concluido';
                    if (lastResponse.message.includes('#DesistirCompra')) action = 'sc_desistir';
                    if (lastResponse.message.includes('#Finalizado')) action = 'sc_checkout_closed';
                }

                if (action === 'sc_abrir_checkout' || lastResponse.message.includes('sc_abrir_checkout')) {
                    action = 'sc_abrir_checkout';
                }

                console.log('[Bot Debug] Processing:', { msg: lastResponse.message, action, state });

                if (action) {
                    const config = getCategoryConfig(ticketRef.current.category);

                    const runInteractiveStep = async () => {
                        if (action === 'sc_falar_atendente') {
                            // Envia mensagem e marca bot como completed para evitar que o sendBotMessage force 'aberto'
                            await sendBotMessage(config.scripts.TRANSFER_STAFF(), { status: 'completed' }, 3000);

                            // Atualiza status para AGUARDANDO STAFF explicitamente
                            try {
                                await updateTicket(ticketRef.current.id, {
                                    status: 'aguardando_staff',
                                    auxiliary_status: ['aguardando_staff', 'resolvido_bot']
                                });
                            } catch (error) {
                                console.error("Error updating ticket status to aguardando_staff:", error);
                                await sendBotMessage("⚠️ Erro ao atualizar status. Por favor, aguarde.", {}, 1000);
                            }
                            return;
                        }

                        if (action === 'sc_gerar_acesso') {
                            await sendBotMessage(config.scripts.ACCESS_GRANTED(), { currentStep: 2 }, 3000);
                            return;
                        }

                        if (action === 'sc_abrir_checkout') {
                            // Apenas atualiza o estado para pausar os timeouts de inatividade
                            await updateBotState({ currentStep: 'checkout_open' });
                            return;
                        }

                        if (action === 'sc_checkout_closed') {
                            await sendBotMessage(config.scripts.CHECKOUT_SURVEY(), { currentStep: 3 }, 5000);
                            return;
                        }

                        if (action === 'sc_concluido') {
                            await sendBotMessage(config.scripts.CONCLUIDO(), { status: 'completed' }, 3000);
                            await updateTicket(ticketRef.current.id, {
                                status: 'resolvido',
                                auxiliary_status: ['resolvido_bot']
                            });
                            return;
                        }

                        if (action === 'sc_desistir') {
                            await sendBotMessage(config.scripts.DESISTIR(), { status: 'completed' }, 3000);
                            await updateTicket(ticketRef.current.id, {
                                status: 'fechado',
                                auxiliary_status: ['cancelado_bot']
                            });
                            return;
                        }
                    };

                    runInteractiveStep();
                }
            }
        }
    }, [ticket?.responses]);

    // 4. Inactivity Timeouts para Fluxos Interativos (Ex: Super Cupom)
    useEffect(() => {
        const state = getBotState();
        const isSuperCupom = ticket?.category === 'super_cupom';

        if (!isSuperCupom) return;
        if (ticket?.assigned_to || ticket?.status === 'resolvido' || ticket?.status === 'fechado' || ticket?.status === 'aguardando_staff') return;
        if (state.status !== 'waiting_input' || state.pendingAction !== 'interactive_flow') return;

        const config = getCategoryConfig(ticket?.category);
        if (!config) return;

        const responses = ticket?.responses || [];
        const lastResponse = responses[responses.length - 1];

        // Se a última msg NÃO foi do bot, significa que o usuário acabou de responder e o bot ainda vai processar.
        if (lastResponse && !lastResponse.isBot && lastResponse.userId !== 'system' && lastResponse.userId !== 'system_bot_security') return;

        let warningTimer = null;
        let closeTimer = null;

        const executeAction = async (actionType) => {
            // Se já tem uma ação desse tipo voando, para.
            if (inFlightActions.current.has(actionType)) return;
            inFlightActions.current.add(actionType);

            try {
                // Revalida estado atual antes de executar p/ evitar race conditions
                if (actionType === 'step1_warning') {
                    // ATUALIZA O ESTADO IMEDIATAMENTE NO BANCO (Antes do delay de digitação) pra travar outros timers
                    await updateBotState({ ...state, warningStep1Sent: true });
                    await sendBotMessage(config.scripts.STEP1_INACTIVITY_WARNING(), {}, 2000);
                } else if (actionType === 'step1_close') {
                    await sendBotMessage(config.scripts.STEP1_INACTIVITY_CLOSE(), { status: 'completed' }, 2000);
                    await updateTicket(ticket.id, { status: 'fechado', auxiliary_status: ['fechado_inatividade'] });
                } else if (actionType === 'step2_warning') {
                    await updateBotState({ ...state, warningStep2Sent: true });
                    await sendBotMessage(config.scripts.STEP2_INACTIVITY_WARNING(), {}, 2000);
                } else if (actionType === 'step2_close') {
                    await sendBotMessage(config.scripts.STEP1_INACTIVITY_CLOSE(), { status: 'completed' }, 2000);
                    await updateTicket(ticket.id, { status: 'fechado', auxiliary_status: ['fechado_inatividade'] });
                } else if (actionType === 'step3_close') {
                    await sendBotMessage(config.scripts.STEP3_INACTIVITY_CLOSE(), { status: 'completed' }, 2000);
                    await updateTicket(ticket.id, { status: 'fechado', auxiliary_status: ['cancelado_bot'] });
                }
            } finally {
                // Remove da trava local após terminar tudo (ou falhar)
                inFlightActions.current.delete(actionType);
            }
        };

        if (state.currentStep === 1) {
            if (!state.warningStep1Sent) {
                warningTimer = setTimeout(() => executeAction('step1_warning'), 2 * 60 * 1000); // 2 minutos
            } else {
                closeTimer = setTimeout(() => executeAction('step1_close'), 5 * 60 * 1000); // 5 minutos
            }
        } else if (state.currentStep === 2) {
            if (!state.warningStep2Sent) {
                warningTimer = setTimeout(() => executeAction('step2_warning'), 5 * 60 * 1000); // 5 minutos
            } else {
                closeTimer = setTimeout(() => executeAction('step2_close'), 5 * 60 * 1000); // 5 minutos
            }
        } else if (state.currentStep === 3) {
            closeTimer = setTimeout(() => executeAction('step3_close'), 3 * 60 * 1000); // 3 minutos
        }
        // Quando for 'checkout_open' (2.5), não tem timer rodando (fica travado)

        return () => {
            if (warningTimer) clearTimeout(warningTimer);
            if (closeTimer) clearTimeout(closeTimer);
        };
    }, [
        ticket?.status,
        ticket?.assigned_to,
        ticket?.responses?.length, // Roda qnd nova msg chega
        ticket?.bot_state?.status,
        ticket?.bot_state?.pendingAction,
        ticket?.bot_state?.currentStep,
        ticket?.bot_state?.warningStep1Sent,
        ticket?.bot_state?.warningStep2Sent
    ]);
};
